import React, { useState } from 'react';

const TIMELINE_STEPS = [
  { key: 'dispatched_at', label: 'Dispatched' },
  { key: 'enroute_at', label: 'En Route' },
  { key: 'checkin_at', label: 'Check-In' },
  { key: 'briefing_at', label: 'Briefing' },
  { key: 'testing_started_at', label: 'Testing' },
  { key: 'completed_at', label: 'Completed' },
];

function formatTime(ts) {
  if (!ts) return null;
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDuration(seconds) {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatTotalDuration(dispatched, completed) {
  if (!dispatched || !completed) return null;
  const seconds = Math.round((new Date(completed) - new Date(dispatched)) / 1000);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function ResultBadge({ result }) {
  if (!result) return <span style={{ color: '#a0aec0' }}>—</span>;
  const isPass = result === 'pass';
  return (
    <span
      style={{
        padding: '0.2rem 0.6rem',
        borderRadius: '9999px',
        fontWeight: 600,
        fontSize: '0.8rem',
        background: isPass ? '#c6f6d5' : '#fed7d7',
        color: isPass ? '#22543d' : '#822727',
      }}
    >
      {result.toUpperCase()}
    </span>
  );
}

function SessionCard({ session }) {
  const [expandedSkills, setExpandedSkills] = useState(new Set());

  const toggleSkill = (id) => {
    setExpandedSkills((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalDur = formatTotalDuration(session.timeline.dispatched_at, session.timeline.completed_at);
  const isActive = session.status === 'active';
  const isAborted = session.status === 'aborted';

  const statusColor = isActive ? '#2b6cb0' : isAborted ? '#c53030' : '#2f855a';
  const statusLabel = isActive ? 'In Progress' : isAborted ? 'Aborted' : 'Completed';

  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        marginBottom: '1rem',
        overflow: 'hidden',
      }}
    >
      {/* Session header */}
      <div
        style={{
          background: '#f7fafc',
          padding: '0.75rem 1rem',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: '1rem' }}>
            {session.station_number ? `Station ${session.station_number} — ` : ''}
            {session.station_name || 'Unknown Station'}
          </strong>
          <span
            style={{
              padding: '0.15rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.75rem',
              fontWeight: 600,
              background: session.session_type === 'retest' ? '#fef3c7' : '#ebf8ff',
              color: session.session_type === 'retest' ? '#92400e' : '#2c5282',
            }}
          >
            {session.session_type === 'retest' ? 'Retest' : 'Initial'}
          </span>
          {session.proctor_name && (
            <span style={{ color: '#718096', fontSize: '0.9rem' }}>
              Proctor: <strong>{session.proctor_name}</strong>
            </span>
          )}
        </div>
        <span
          style={{
            padding: '0.2rem 0.6rem',
            borderRadius: '4px',
            fontSize: '0.8rem',
            fontWeight: 600,
            background: isActive ? '#ebf8ff' : isAborted ? '#fff5f5' : '#f0fff4',
            color: statusColor,
          }}
        >
          {statusLabel}
        </span>
      </div>

      <div style={{ padding: '1rem' }}>
        {/* Timeline */}
        <div style={{ marginBottom: '1rem' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.25rem',
              fontSize: '0.85rem',
            }}
          >
            {TIMELINE_STEPS.map((step, i) => {
              const ts = session.timeline[step.key];
              const active = !!ts;
              return (
                <React.Fragment key={step.key}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      background: active ? '#ebf8ff' : '#f7fafc',
                      border: `1px solid ${active ? '#bee3f8' : '#e2e8f0'}`,
                      minWidth: '70px',
                    }}
                  >
                    <span style={{ fontWeight: 600, color: active ? '#2b6cb0' : '#a0aec0', fontSize: '0.75rem' }}>
                      {step.label}
                    </span>
                    <span style={{ color: active ? '#4a5568' : '#cbd5e0', fontSize: '0.75rem' }}>
                      {ts ? formatTime(ts) : '—'}
                    </span>
                  </div>
                  {i < TIMELINE_STEPS.length - 1 && (
                    <span style={{ color: '#cbd5e0', fontSize: '0.75rem' }}>→</span>
                  )}
                </React.Fragment>
              );
            })}
            {totalDur && (
              <span style={{ marginLeft: '0.5rem', color: '#718096', fontSize: '0.8rem' }}>
                ({totalDur} total)
              </span>
            )}
          </div>
        </div>

        {/* Skills table */}
        {session.skills.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#f7fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#4a5568' }}>
                  Skill
                </th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: 600, color: '#4a5568', width: '80px' }}>
                  Result
                </th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: 600, color: '#4a5568', width: '80px' }}>
                  Duration
                </th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#4a5568' }}>
                  Recorded By
                </th>
                <th style={{ padding: '0.5rem 0.75rem', width: '90px' }} />
              </tr>
            </thead>
            <tbody>
              {session.skills.map((skill) => (
                <React.Fragment key={skill.assignment_id}>
                  <tr
                    style={{
                      borderBottom: expandedSkills.has(skill.assignment_id)
                        ? 'none'
                        : '1px solid #edf2f7',
                    }}
                  >
                    <td style={{ padding: '0.5rem 0.75rem' }}>{skill.skill_name}</td>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                      <ResultBadge result={skill.result} />
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center', color: '#718096' }}>
                      {formatDuration(skill.duration_seconds)}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', color: '#718096' }}>
                      {skill.result_actor_name || '—'}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
                      {skill.checklist_items.length > 0 && (
                        <button
                          onClick={() => toggleSkill(skill.assignment_id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#4a90d9',
                            fontSize: '0.8rem',
                            padding: '0.2rem 0.4rem',
                          }}
                        >
                          {expandedSkills.has(skill.assignment_id) ? '▲ Hide' : '▼ Items'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedSkills.has(skill.assignment_id) && (
                    <tr>
                      <td
                        colSpan={5}
                        style={{ padding: '0 0.75rem 0.5rem 2rem', borderBottom: '1px solid #edf2f7' }}
                      >
                        {skill.checklist_items.map((item, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              padding: '0.2rem 0',
                              fontSize: '0.85rem',
                              color: '#4a5568',
                            }}
                          >
                            <span
                              style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                background:
                                  item.result === 'pass'
                                    ? '#38a169'
                                    : item.result === 'fail'
                                    ? '#e53e3e'
                                    : '#a0aec0',
                                flexShrink: 0,
                              }}
                            />
                            <span>{item.description}</span>
                            {item.is_critical && (
                              <span
                                style={{
                                  fontSize: '0.7rem',
                                  color: '#c05621',
                                  fontWeight: 600,
                                  background: '#fef3c7',
                                  padding: '0.1rem 0.3rem',
                                  borderRadius: '3px',
                                }}
                              >
                                CRITICAL
                              </span>
                            )}
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ color: '#a0aec0', fontSize: '0.9rem', margin: 0 }}>No skill data recorded.</p>
        )}
      </div>
    </div>
  );
}

export default function CompanyDetailPanel({ company, onClose }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #bee3f8',
        borderRadius: '8px',
        padding: '1.25rem',
        marginTop: '0.5rem',
        marginBottom: '1rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{company.name}</h3>
          {(company.home_department || company.program_name) && (
            <p style={{ margin: '0.25rem 0 0', color: '#718096', fontSize: '0.9rem' }}>
              {[company.home_department, company.program_name].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1.2rem',
            color: '#718096',
            lineHeight: 1,
          }}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      {company.sessions.length === 0 ? (
        <p style={{ color: '#a0aec0' }}>No sessions recorded.</p>
      ) : (
        company.sessions.map((session) => (
          <SessionCard key={session.id} session={session} />
        ))
      )}
    </div>
  );
}
